// ToggleField — canonical `toggle` field primitive (comp-ε GAP-3b-ii-a,
// CHG-2449). A REAL accessible on/off switch — NOT a CheckboxField alias.
// `toggle` is the dominant boolean field_type across tier-05-experience
// (54× vs `checkbox` 12×); EventCreate/EventEdit use it for is_public /
// all_day / is_virtual / registration_enabled. Storage: boolean.
//
// Why a switch and not a checkbox: a switch reads as an immediate on/off
// state change (no separate "submit" mental model), whereas a checkbox reads
// as "selected for an action". Design-system correctness per the PM scope
// decision — distinct visual track+thumb, distinct ARIA role.
//
// Contract: specs/_product/field-primitive-registry.md §1 ('toggle') + §2.
// Doctrine: feedback_field_primitive_components_canonical.md.
// a11y (§2): built on the W3C APG "switch (button)" pattern — a
// <button role="switch"> with an explicit aria-checked reflecting the boolean.
// The accessible name comes from a <span id> referenced via aria-labelledby
// (a <button> is not a labelable element, so htmlFor/label-wrapping — the
// CheckboxField mechanism — does not apply; aria-labelledby is the idiomatic
// switch-name association). Validation error renders in a role="alert" live
// region; aria-invalid + aria-required on the button; aria-describedby links
// the button to its error + helper text nodes (WCAG 2.1 SC 1.3.1 / 3.3.1 /
// 4.1.2). The id triplet + describedBy() come from the shared FieldShell
// helpers, identical to the 5 basic primitives (CHG-2433/2448).

import type { FieldPrimitiveProps } from "./types";
import { useFieldIds, describedBy } from "./FieldShell";

export type ToggleFieldProps = FieldPrimitiveProps<boolean>;

export function ToggleField({
  name,
  value,
  onChange,
  label,
  error,
  disabled,
  required,
  helperText,
}: ToggleFieldProps) {
  const ids = useFieldIds();
  const labelId = `${ids.inputId}-label`;
  const checked = value ?? false;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <button
          id={ids.inputId}
          name={name}
          type="button"
          role="switch"
          aria-checked={checked}
          aria-invalid={error ? true : undefined}
          aria-required={required || undefined}
          aria-describedby={describedBy(ids, error, helperText)}
          aria-labelledby={label ? labelId : undefined}
          disabled={disabled}
          onClick={() => onChange(!checked)}
          style={{
            position: "relative",
            flexShrink: 0,
            width: 36,
            height: 20,
            padding: 0,
            borderRadius: 9999,
            border: error
              ? "1px solid var(--color-error)"
              : checked
                ? "1px solid var(--color-primary)"
                : "1px solid var(--color-border-strong)",
            background: checked
              ? "var(--color-primary)"
              : "var(--color-border)",
            cursor: disabled ? "default" : "pointer",
            opacity: disabled ? 0.6 : 1,
            transition: "background 120ms ease",
          }}
        >
          <span
            aria-hidden="true"
            style={{
              position: "absolute",
              top: 1,
              left: checked ? 17 : 1,
              width: 16,
              height: 16,
              borderRadius: "50%",
              background: "var(--color-surface)",
              boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
              transition: "left 120ms ease",
            }}
          />
        </button>
        {label ? (
          <span
            id={labelId}
            style={{
              fontSize: 13,
              color: "var(--color-text)",
              cursor: disabled ? "default" : "pointer",
            }}
          >
            {label}{" "}
            {required ? (
              <span aria-hidden="true" style={{ color: "var(--color-error)" }}>
                *
              </span>
            ) : null}
          </span>
        ) : null}
      </div>
      {helperText ? (
        <small
          id={ids.helperId}
          style={{ color: "var(--color-text-muted)", paddingLeft: 44 }}
        >
          {helperText}
        </small>
      ) : null}
      {error ? (
        <small
          id={ids.errorId}
          role="alert"
          style={{ color: "var(--color-error)", paddingLeft: 44 }}
        >
          {error}
        </small>
      ) : null}
    </div>
  );
}
